import { inject } from "@angular/core";
import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { catchError, Observable, tap, throwError } from "rxjs";
import { OpsHttpInspectorService } from "src/app/pages/ops-ui/services/ops-http-inspector.service";

const SKIP_URL_PATTERN = /\/assets\//;
const SENSITIVE_HEADERS = new Set(["authorization"]);

function maskSensitiveHeaderValue(value: string): string {
  const [scheme] = value.split(" ");
  return scheme ? `${scheme} ••••••` : "••••••";
}

function headersToRecord(headers: HttpHeaders): Record<string, string> {
  return headers.keys().reduce<Record<string, string>>((acc, key) => {
    const value = headers.get(key) ?? "";
    acc[key] = SENSITIVE_HEADERS.has(key.toLowerCase())
      ? maskSensitiveHeaderValue(value)
      : value;
    return acc;
  }, {});
}

function serializeRequestBody(body: unknown): unknown {
  if (body instanceof FormData) {
    const entries: Record<string, unknown> = {};
    body.forEach((value, key) => {
      entries[key] = value instanceof File ? `[File] ${value.name}` : value;
    });
    return entries;
  }
  return body ?? null;
}

/**
 * Records every HTTP call into `OpsHttpInspectorService` so the Ops Console
 * inspector bar can always render the last request/response, even for calls
 * made outside `/ops` routes.
 */
export const opsHttpInspectorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<any> => {
  if (SKIP_URL_PATTERN.test(req.url)) {
    return next(req);
  }

  const inspector = inject(OpsHttpInspectorService);
  const entryId = inspector.startEntry(
    req.method,
    req.urlWithParams,
    headersToRecord(req.headers),
    serializeRequestBody(req.body),
  );

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        inspector.completeEntry(entryId, {
          status: "success",
          statusCode: event.status,
          statusText: event.statusText,
          responseHeaders: headersToRecord(event.headers),
          responseBody: event.body,
        });
      }
    }),
    catchError((error: HttpErrorResponse) => {
      inspector.completeEntry(entryId, {
        status: "error",
        statusCode: error.status,
        statusText: error.statusText,
        responseHeaders: error.headers ? headersToRecord(error.headers) : {},
        responseBody: error.error ?? null,
        errorMessage: error.message,
      });
      return throwError(() => error);
    }),
  );
};
